import React, { useEffect, useMemo, useState } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import { composeOptimizedImageUrl } from 'lib/utils'

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  mainImageWrapper: {
    width: '100%',
    height: 240,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    background: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    [theme.breakpoints.up('sm')]: {
      height: 260,
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainImage: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'contain',
  },
  thumbnailsRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    overflowX: 'auto',
    paddingBottom: theme.spacing(0.5),
  },
  thumbButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  thumbImage: {
    width: 64,
    height: 64,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    border: '2px solid transparent',
  },
  thumbImageSelected: {
    borderColor: theme.palette.primary.main,
  },
  ctaRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
  },
  ctaButton: {
    width: '100%',
    height: 56,
    borderRadius: 12,
    fontSize: '1rem',
    fontWeight: 700,
  },
  ctaButtonContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sideToggleRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  sideToggleText: {
    padding: 0,
    margin: 0,
    background: 'transparent',
    border: '1px solid transparent',
    color: '#000',
    cursor: 'pointer',
    fontSize: '0.875rem',
    lineHeight: 1.2,
    fontWeight: 600,
    borderRadius: 2,
    boxSizing: 'border-box',
    '&:hover': {
      borderColor: '#000',
    },
  },
  sideToggleTextSelected: {
    borderColor: '#000',
  },
  colorRadioRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
  colorRadioButton: {
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  colorRadioCircle: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '2px solid transparent',
    boxSizing: 'border-box',
  },
  colorRadioCircleSelected: {
    borderColor: '#000',
    boxShadow: '0 0 0 2px #fff inset',
  },
  colorLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  sidePreviewRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
  sidePreviewButton: {
    appearance: 'none',
    background: 'transparent',
    border: '2px solid transparent',
    padding: 0,
    cursor: 'pointer',
    width: 68,
    height: 44,
    borderRadius: 8,
    boxSizing: 'border-box',
    '&:hover': {
      borderColor: '#000',
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  sidePreviewButtonSelected: {
    borderColor: '#000',
  },
  sidePreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
}))

export default function JrlMerchProductCard({
  product,
  className = '',
  // Injected by SliderNew so we only preload when the card is visible-ish.
  isMerchPreloadCandidate = false,
}) {
  const classes = useStyles()
  const MAIN_IMAGE_WIDTH = 960
  const THUMB_IMAGE_WIDTH = 240
  const SIDE_PREVIEW_IMAGE_WIDTH = 240

  const getColorSwatch = colorLabel => {
    const v = String(colorLabel || '').toLowerCase()
    if (v.includes('black')) return '#0b1f3a'
    if (v.includes('navy')) return '#163B6D'
    if (v.includes('royal')) return '#1E3A8A'
    if (v.includes('cardinal')) return '#7A0C22'
    if (v.includes('green') || v.includes('sage')) return '#0B6B3A'
    if (v.includes('grey') || v.includes('gray')) return '#8A8A8A'
    if (v.includes('melange')) return '#7A7A7A'
    if (v.includes('white')) return '#FFFFFF'
    return '#000000'
  }

  const variants = useMemo(
    () => product?.variants || [],
    // `product?.variants` is included so the hook dependency linter stays happy.
    [product?.variants],
  )

  const [selectedVariantId, setSelectedVariantId] = useState(
    variants[0]?.id || '',
  )
  const [selectedSide, setSelectedSide] = useState('front')

  useEffect(() => {
    if (!variants.length) {
      setSelectedVariantId('')
      return
    }

    const exists = variants.some(v => v.id === selectedVariantId)
    if (!exists) setSelectedVariantId(variants[0].id)
  }, [product?.id, variants, selectedVariantId])

  const selectedVariant =
    variants.find(v => v.id === selectedVariantId) || variants[0]

  const preloadImage = (url, width) => {
    if (!url) return
    const optimizedUrl = composeOptimizedImageUrl(url, width)
    if (!optimizedUrl) return
    // Warm browser cache. Creating an Image object is enough for eager download.
    const img = new window.Image()
    img.src = optimizedUrl
  }

  useEffect(() => {
    // When the color changes, show the front image by default.
    setSelectedSide('front')
  }, [selectedVariantId])

  useEffect(() => {
    if (!selectedVariant) return
    // If the slide is already a preload candidate, we preload all variants below,
    // so skip per-variant preloading here to avoid duplicate network work.
    if (isMerchPreloadCandidate) return

    const frontUrl = selectedVariant.frontImageUrl || selectedVariant.imageUrl
    if (frontUrl) {
      preloadImage(frontUrl, MAIN_IMAGE_WIDTH)
    }
    if (selectedVariant.backImageUrl) {
      preloadImage(selectedVariant.backImageUrl, MAIN_IMAGE_WIDTH)
    }
  }, [selectedVariant, selectedVariantId, isMerchPreloadCandidate])

  useEffect(() => {
    // Preload all colors (front + back) for this merch card.
    if (!isMerchPreloadCandidate) return
    if (!variants?.length) return

    for (const v of variants) {
      const front = v.frontImageUrl || v.imageUrl
      if (front) {
        preloadImage(front, MAIN_IMAGE_WIDTH)
        preloadImage(front, THUMB_IMAGE_WIDTH)
      }
      if (v.backImageUrl) {
        preloadImage(v.backImageUrl, MAIN_IMAGE_WIDTH)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMerchPreloadCandidate, variants])

  if (!selectedVariant) return null

  const mainImageUrl =
    selectedSide === 'back'
      ? selectedVariant.backImageUrl || selectedVariant.imageUrl
      : selectedVariant.frontImageUrl || selectedVariant.imageUrl

  const mainImageAlt =
    selectedVariant.imageAlt ||
    `${product?.model || 'Merch'} - ${selectedVariant.colorLabel}`

  const subtitleParts = []
  if (product?.category) subtitleParts.push(product.category)
  if (product?.gender) subtitleParts.push(product.gender)
  const subtitle = subtitleParts.join(' · ')

  return (
    <Box
      className={`${className} ${classes.root}`}
      data-testid="jrl-merch-card"
    >
      <Typography variant="h6" className={classes.title} gutterBottom>
        {product?.title || product?.model}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.subtitle}
        >
          {subtitle}
        </Typography>
      )}

      {selectedVariant?.colorLabel && (
        <Typography variant="body2" className={classes.colorLabel}>
          Color:&nbsp;{selectedVariant.colorLabel}
        </Typography>
      )}

      <Box className={classes.mainImageWrapper}>
        <img
          className={classes.mainImage}
          src={composeOptimizedImageUrl(mainImageUrl, 960)}
          alt={mainImageAlt}
          data-testid="jrl-merch-main-image"
        />
      </Box>

      {selectedVariant.backImageUrl && (
        <Box className={classes.sidePreviewRow} aria-label="Select image side">
          <button
            type="button"
            aria-label="Front"
            className={`${classes.sidePreviewButton} ${
              selectedSide === 'front' ? classes.sidePreviewButtonSelected : ''
            }`}
            onClick={() => setSelectedSide('front')}
          >
            <img
              className={classes.sidePreviewImage}
              src={composeOptimizedImageUrl(
                selectedVariant.frontImageUrl || selectedVariant.imageUrl,
                SIDE_PREVIEW_IMAGE_WIDTH,
              )}
              alt="Front"
            />
          </button>
          <button
            type="button"
            aria-label="Back"
            className={`${classes.sidePreviewButton} ${
              selectedSide === 'back' ? classes.sidePreviewButtonSelected : ''
            }`}
            onClick={() => setSelectedSide('back')}
          >
            <img
              className={classes.sidePreviewImage}
              src={composeOptimizedImageUrl(
                selectedVariant.backImageUrl || selectedVariant.imageUrl,
                SIDE_PREVIEW_IMAGE_WIDTH,
              )}
              alt="Back"
            />
          </button>
        </Box>
      )}

      {variants.length > 1 && (
        <Box className={classes.colorRadioRow} aria-label="Select color">
          {variants.map(variant => {
            const selected = variant.id === selectedVariantId
            const swatch = getColorSwatch(variant.colorLabel)
            return (
              <button
                type="button"
                key={variant.id}
                className={classes.colorRadioButton}
                aria-pressed={selected}
                aria-label={variant.colorLabel}
                onClick={() => {
                  const frontUrl = variant.frontImageUrl || variant.imageUrl
                  if (frontUrl) preloadImage(frontUrl, MAIN_IMAGE_WIDTH)
                  if (variant.backImageUrl) {
                    preloadImage(variant.backImageUrl, MAIN_IMAGE_WIDTH)
                  }
                  setSelectedVariantId(variant.id)
                  setSelectedSide('front')
                }}
              >
                <span
                  className={`${classes.colorRadioCircle} ${
                    selected ? classes.colorRadioCircleSelected : ''
                  }`}
                  style={{ backgroundColor: swatch }}
                />
              </button>
            )
          })}
        </Box>
      )}

      {selectedVariant.purchaseUrl && (
        <Box className={classes.ctaRow}>
          <Button
            color="primary"
            variant="contained"
            href={selectedVariant.purchaseUrl}
            target="_blank"
            rel="noreferrer"
            className={classes.ctaButton}
          >
            <span className={classes.ctaButtonContent}>
              <span>Shop Now</span>
              <ChevronRightIcon />
            </span>
          </Button>
        </Box>
      )}
    </Box>
  )
}
